import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { CheckboxLineIcon } from "@twilio-paste/icons/esm/CheckboxLineIcon";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { Button } from "@twilio-paste/core/button";
import { useEffect, useState } from "react";
import { createSelector } from "reselect";
import { sessionDataHandler } from "../sessionDataHandler";
import { endSession } from "../store/actions/endActions";
import { changeExpandedStatus } from "../store/actions/genericActions";
import { ChatReducer } from "../store/chat.reducer";
import { AppState, EngagementPhase } from "../store/definitions";
import { SessionReducer } from "../store/session.reducer";
import { ConfirmationModal } from "./ConfirmationModal";
import { webchatContainerStyles, webchatLogoStyles, webchatSubHeaderStyles, webchatTitleStyles } from "./styles/Header.styles";
export const headerSelector = createSelector(
    (state: AppState) => state,
    (state) => ({
        conversation: state.chat.conversation,
        isConversationActive: !!state.chat.conversation?.sid && state.chat.conversationState === "active",
        mainHeader: state.config.theme.content?.header ?? state.config.theme.content?.channelSelection?.header,
        subHeader: state.config.theme.content?.subHeader ?? state.config.theme.content?.channelSelection?.subHeader,
        subHeaderType: state.config.theme.content?.subHeaderType,
        subHeaderScheduleId: state.config.theme.content?.subHeaderScheduleId,
        botName: state.config.bot.botName ?? "",
        isCloseButtonEnabled: state.config.flags.closeButtonEnabled ?? false,
        logoUrl: state.config.theme.content?.logoUrl,
        enableDragFlag: state.config.flags?.dragEnabled,
        enableChannelSelection: state.config.flags?.channelSelectionEnabled,
        enableTranscript: state.config.flags.downloadTranscriptEnabled
    })
);

export const Header = ({ customTitle }: { customTitle?: string; }) => {
    const dispatch = useDispatch<ThunkDispatch<ChatReducer | SessionReducer, unknown, AnyAction>>();
    const { conversation, isConversationActive, mainHeader, subHeader, subHeaderType, subHeaderScheduleId, botName, isCloseButtonEnabled, logoUrl, enableDragFlag, enableChannelSelection, enableTranscript } =
        useSelector(headerSelector);
    const [evaluatedSubHeader, setEvaluatedSubHeader] = useState<string | undefined>(undefined);

    const handleCloseWebchat = () => {
        enableChannelSelection
            ? dispatch(endSession(botName, conversation, true, EngagementPhase.ChannelSelection))
            : dispatch(endSession(botName, conversation, true, EngagementPhase.PreEngagementForm))
    };

    const handleMinimize = () => {
        dispatch(changeExpandedStatus({ expanded: false }));
    };

    useEffect(() => {
        if (subHeaderType === "dynamic") {
            sessionDataHandler.getScheduledSubHeader(subHeaderScheduleId, subHeader).then((subheader) => {
                setEvaluatedSubHeader(subheader);
            });
        } else {
            setEvaluatedSubHeader(subHeader);
        }
    }, [subHeaderType, subHeaderScheduleId]);

    const modalBodyText2 = enableTranscript ? "Before closing, you have the option to download a copy of your conversation using the button below." : "";

    return (
        <Box
            {...webchatContainerStyles}
            className={enableDragFlag ? "draggable-handle" : ""}
            style={{ cursor: enableDragFlag ? "move" : "default" }}
        >
            <Box display="flex" justifyContent="left" alignItems="center">
                {logoUrl && <Box as="img" {...webchatLogoStyles} src={logoUrl} alt="Logo" draggable={false} />}
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent={subHeader ? "normal" : "center"}
                    alignItems={subHeader ? "flex-start" : "center"}
                >
                    <Text as="h3" {...webchatTitleStyles}>
                        {mainHeader !== undefined
                            ? mainHeader
                            : customTitle !== undefined
                                ? customTitle
                                : "Live Chat"}
                    </Text>
                    {subHeader && (
                        <Text as="h3" {...webchatSubHeaderStyles}>
                            {evaluatedSubHeader}
                        </Text>
                    )}
                </Box>
            </Box>
            <Box display="flex" marginRight="space30" columnGap="space30">
                {isMobile && (
                    <Button variant="secondary_icon" size="circle" onClick={handleMinimize}>
                        <CheckboxLineIcon decorative={false} title="Minimize chat" color="colorTextBrandInverse" />
                    </Button>
                )}
                {isCloseButtonEnabled && isConversationActive && (
                    <>
                        <ConfirmationModal
                            triggerButtonText="Close Chat"
                            modalHeading="Close Chat"
                            modalBodyText1="Are you sure you want to end this chat session?"
                            modalBodyText2={modalBodyText2}
                            confirmButtonText="Close"
                            cancelButtonText="Cancel"
                            downloadButtonText="Download Transcript"
                            alertHeading="Close Chat"
                            alertBodyText="Are you sure you want to close the chat? This will stop the conversation."
                            alertConfirmLabel="Yes, close"
                            alertDismissLabel="Cancel"
                            onConfirm={handleCloseWebchat}
                            destructive={true}
                        />
                    </>
                )}
            </Box>
        </Box>
    );
};
